import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { apiJson, fetchAuthedBlob } from '../api/client.js';
import { formatDateTime } from '../utils/formatDate.js';

function initials(name) {
  return String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

function taskBase(mode, taskId) {
  return mode === 'client' ? `/api/my/tasks/${taskId}` : `/api/admin/tasks/${taskId}`;
}

/**
 * Comments and file attachments inside a task card.
 * Screenshots can be uploaded, dropped, or pasted.
 */
export const TaskCardActivity = forwardRef(function TaskCardActivity({ mode, taskId, onError, onCounts }, ref) {
  const [activities, setActivities] = useState([]);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentBody, setCommentBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState({});
  const [dropActive, setDropActive] = useState(false);
  const fileRef = useRef(null);
  const base = taskBase(mode, taskId);

  const applyPayload = (payload) => {
    const nextComments = payload.comments || [];
    const nextAttachments = payload.attachments || [];
    setActivities(payload.activities || []);
    setComments(nextComments);
    setAttachments(nextAttachments);
    onCounts?.({ comments: nextComments.length, attachments: nextAttachments.length });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setCommentBody('');
    apiJson(`${base}/activity`)
      .then((payload) => {
        if (!cancelled) applyPayload(payload);
      })
      .catch((error) => {
        if (!cancelled) onError?.(error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [base]);

  useEffect(() => {
    let cancelled = false;
    const created = [];
    const images = attachments.filter((file) => file.is_image);
    if (!images.length) {
      setPreviews({});
      return undefined;
    }
    (async () => {
      const next = {};
      for (const file of images) {
        try {
          const blob = await fetchAuthedBlob(`${base}/attachments/${file.id}`);
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          created.push(url);
          next[file.id] = url;
        } catch {
          // The file row still shows the name if the preview cannot load.
        }
      }
      if (!cancelled) setPreviews(next);
    })();
    return () => {
      cancelled = true;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [base, attachments]);

  const uploadFiles = async (fileList) => {
    const files = [...fileList].filter(Boolean);
    if (!files.length) return;
    setUploading(true);
    try {
      let payload = null;
      for (const file of files) {
        const body = new FormData();
        body.append('file', file);
        payload = await apiJson(`${base}/attachments`, { method: 'POST', body });
      }
      if (payload) applyPayload(payload);
    } catch (error) {
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  const postComment = async (event) => {
    event.preventDefault();
    const body = commentBody.trim();
    if (!body) return;
    setPosting(true);
    try {
      const payload = await apiJson(`${base}/comments`, { method: 'POST', body: { body } });
      applyPayload(payload);
      setCommentBody('');
    } catch (error) {
      onError?.(error);
    } finally {
      setPosting(false);
    }
  };

  const removeComment = async (comment) => {
    try {
      applyPayload(await apiJson(`${base}/comments/${comment.id}`, { method: 'DELETE' }));
    } catch (error) {
      onError?.(error);
    }
  };

  const removeAttachment = async (file) => {
    try {
      applyPayload(await apiJson(`${base}/attachments/${file.id}`, { method: 'DELETE' }));
    } catch (error) {
      onError?.(error);
    }
  };

  const downloadFile = async (file) => {
    try {
      const blob = await fetchAuthedBlob(`${base}/attachments/${file.id}`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.original_name || 'attachment';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      onError?.(error);
    }
  };

  const uploadClipboard = (event) => {
    const image = [...(event.clipboardData?.items || [])].find((item) => item.type.startsWith('image/'));
    if (!image) return;
    const file = image.getAsFile();
    if (!file) return;
    event.preventDefault();
    void uploadFiles([file]);
  };

  useImperativeHandle(ref, () => ({ uploadClipboard }));

  return (
    <div className="task-activity">
      {activities.length ? (
        <div style={{ marginBottom: '0.75rem' }}>
          <h4>Activity</h4>
          <ul className="task-comments">
            {activities.map((entry) => (
              <li key={entry.id}>
                <span>{entry.sentence}</span>
                <span className="muted small"> {formatDateTime(entry.created_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="task-activity-head">
        <h4>Attachments</h4>
        <button type="button" className="btn-secondary" disabled={uploading} onClick={() => fileRef.current?.click()}>
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        <input
          ref={fileRef}
          type="file"
          hidden
          multiple
          accept="image/*,.pdf,.txt,.doc,.docx,.xls,.xlsx,.zip"
          onChange={(event) => {
            void uploadFiles(event.target.files);
            event.target.value = '';
          }}
        />
      </div>
      <div
        className={`task-dropzone${dropActive ? ' is-active' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDropActive(true);
        }}
        onDragLeave={() => setDropActive(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDropActive(false);
          void uploadFiles(event.dataTransfer.files);
        }}
      >
        {loading ? <p className="muted small">Loading…</p> : null}
        {!loading && !attachments.length ? (
          <p className="muted small">Drop images here, or paste a screenshot into this card.</p>
        ) : null}
        <div className="task-attachment-grid">
          {attachments.map((file) => (
            <figure key={file.id} className="task-attachment">
              {file.is_image && previews[file.id] ? (
                <button type="button" className="task-attachment-preview" onClick={() => void downloadFile(file)}>
                  <img src={previews[file.id]} alt={file.original_name} />
                </button>
              ) : (
                <button type="button" className="task-attachment-file" onClick={() => void downloadFile(file)}>
                  {file.original_name}
                </button>
              )}
              <figcaption>
                <span title={file.original_name}>{file.original_name}</span>
                {file.can_delete ? (
                  <button type="button" onClick={() => void removeAttachment(file)}>
                    Remove
                  </button>
                ) : null}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      <h4>Comments & Updates</h4>
      {loading ? null : !comments.length ? <p className="muted small">No comments yet.</p> : null}
      <ul className="task-comments">
        {comments.map((comment) => (
          <li key={comment.id} className="task-comment">
            <span className="task-avatar" title={comment.user?.name || 'Someone'}>
              {initials(comment.user?.name)}
            </span>
            <div className="task-comment-body">
              <div className="task-comment-meta">
                <strong>{comment.user?.name || 'Someone'}</strong>
                <span className="muted small">{formatDateTime(comment.created_at)}</span>
                {comment.can_delete ? (
                  <button type="button" onClick={() => void removeComment(comment)}>
                    Delete
                  </button>
                ) : null}
              </div>
              <p>{comment.body}</p>
            </div>
          </li>
        ))}
      </ul>
      <form className="task-comment-form" onSubmit={postComment}>
        <textarea
          rows={3}
          value={commentBody}
          maxLength={5000}
          placeholder="Write a comment…"
          onChange={(event) => setCommentBody(event.target.value)}
        />
        <button type="submit" className="btn-primary" disabled={posting || !commentBody.trim()}>
          {posting ? 'Sending…' : 'Comment'}
        </button>
      </form>
    </div>
  );
});
